/**
 * FinancialSummary Widget (T053, T168)
 * Weekly/monthly revenue, net revenue, and cash flow metrics
 */

import { TrendingUp, Calendar, Users, DollarSign } from 'lucide-react';
import type { DashboardData } from '../../hooks/useDashboard';

interface Props {
  metrics: DashboardData['metrics'];
}

export default function FinancialSummary({ metrics }: Props) {
  const netRevenue =
    metrics.thisMonth.netRevenue ?? metrics.thisMonth.revenue - (metrics.thisMonth.expenses ?? 0);
  const isPositive = netRevenue >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      <div className="claude-card p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">Monthly Revenue</p>
            <p className="text-2xl font-semibold tracking-tight mt-1">
              {metrics.thisMonth.revenueFormatted || '$0.00'}
            </p>
          </div>
        </div>
      </div>

      {/* Net Revenue Widget (T168) */}
      <div className="claude-card p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div
            className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isPositive ? 'bg-success/10' : 'bg-destructive/10'
            }`}
          >
            <DollarSign className={`w-6 h-6 ${isPositive ? 'text-success' : 'text-destructive'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">Net Revenue</p>
            <p
              className={`text-2xl font-semibold tracking-tight mt-1 ${
                isPositive ? '' : 'text-destructive'
              }`}
            >
              {metrics.thisMonth.netRevenueFormatted || '$0.00'}
            </p>
            {metrics.thisMonth.expenses != null && metrics.thisMonth.expenses > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {metrics.thisMonth.expensesFormatted} in expenses
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="claude-card p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">Monthly Appointments</p>
            <p className="text-2xl font-semibold tracking-tight mt-1">
              {metrics.thisMonth.appointments || 0}
            </p>
          </div>
        </div>
      </div>

      <div className="claude-card p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-success" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">New Customers</p>
            <p className="text-2xl font-semibold tracking-tight mt-1">
              {metrics.thisMonth.newCustomers || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
